import { useState, useRef, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { BASE_URL } from "../../apiConfig"
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import axios from "axios"
import Select from "react-select"
import RichTextEditor from "../RichTextEditor"

export default function CreateProductForm({ categories, availableClients, availableVariants, Fournisseurs }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    achat_price: "",
    saller_price: "",
    selling_price: "",
    stock: "",
    status: "active",
    is_global: true,
    variant: false,
    category_id: "",
    fournisseur_id: "",
    image_urls: "",
    product_expired_soon: false,
    discount: "",
    discount_start_date: "",
    discount_end_date: "",
    rating: 0,
  })

  const [primaryImage, setPrimaryImage] = useState(null)
  const [has_discount, setHas_discount] = useState(false)
  const [images, setImages] = useState([])
  const [selectedVariants, setSelectedVariants] = useState([])
  const [selectedClients, setSelectedClients] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const [dragActivePrimary, setDragActivePrimary] = useState(false)
  const [dragActiveAdditional, setDragActiveAdditional] = useState(false)
  const primaryFileInputRef = useRef(null)
  const additionalFileInputRef = useRef(null)
  const [selectedVariantTypes, setSelectedVariantTypes] = useState([])
  const { token } = useAuth()
  const { t, locale } = useLanguage();

  const colorVariants = availableVariants.filter((v) => v.type === "color")
  const sizeVariants = availableVariants.filter((v) => v.type === "size")
  const sizeRank = {
    xxs: 0,
    xs: 1,
    s: 2,
    m: 3,
    l: 4,
    xl: 5,
    xxl: 6,
    xxxl: 7,
    xxxxl: 8,
    xxxxxl: 9,
    xxxxxxl: 10,
  }

  const normalizeSize = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/-/g, "")

    const xlMatch = normalized.match(/^(\d+)xl$/)
    if (xlMatch) {
      const count = Number(xlMatch[1])
      if (!Number.isNaN(count) && count > 0) {
        return `${"x".repeat(count)}l`
      }
    }

    return normalized
  }

  const sortedSizeVariants = useMemo(() => {
    return [...sizeVariants].sort((a, b) => {
      const aValue = normalizeSize(a.value)
      const bValue = normalizeSize(b.value)

      const aRank = sizeRank[aValue]
      const bRank = sizeRank[bValue]
      const aKnown = aRank !== undefined
      const bKnown = bRank !== undefined

      if (aKnown && bKnown) return aRank - bRank
      if (aKnown) return -1
      if (bKnown) return 1

      const aNumber = Number(aValue)
      const bNumber = Number(bValue)
      const aIsNumber = !Number.isNaN(aNumber)
      const bIsNumber = !Number.isNaN(bNumber)

      if (aIsNumber && bIsNumber) return aNumber - bNumber
      if (aIsNumber) return 1
      if (bIsNumber) return -1

      return aValue.localeCompare(bValue)
    })
  }, [sizeVariants])

  useEffect(() => {
    if (!formData.variant || selectedVariantTypes.length > 0) return
    if (colorVariants.length > 0 && sizeVariants.length > 0) {
      setSelectedVariantTypes(["color", "size"])
    } else if (colorVariants.length > 0) {
      setSelectedVariantTypes(["color"])
    } else if (sizeVariants.length > 0) {
      setSelectedVariantTypes(["size"])
    }
  }, [formData.variant, selectedVariantTypes.length, colorVariants.length, sizeVariants.length])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === "variant") {
      setFormData((prev) => ({ ...prev, variant: checked }))
      if (!checked) {
        setVariantMatrix({})
        setSelectedVariantTypes([])
      }
      if (errors.variant || errors.variantTypes || errors.variants) {
        setErrors((prev) => ({
          ...prev,
          variant: "",
          variantTypes: "",
          variants: "",
        }))
      }
      return
    }
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    })
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  const handlePrimaryImageUpload = (files) => {
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith("image/")) {
        setPrimaryImage({ file, preview: URL.createObjectURL(file) }) // Store the file and its preview URL
        setErrors((prev) => ({ ...prev, primaryImage: "" })) // Clear any previous errors
      } else {
        setPrimaryImage(null)
        setErrors((prev) => ({ ...prev, primaryImage: t('validation.image_type') }))
        toast.error(t('validation.image_type_toast'))
      }
    } else {
      setPrimaryImage(null)
    }
  }

  const removePrimaryImage = () => {
    setPrimaryImage(null)
    setErrors((prev) => ({ ...prev, primaryImage: "" }))
  }

  const handleAdditionalImageUpload = (files) => {
    if (files && files.length > 0) {
      const newValidImages = Array.from(files)
        .filter((file) => {
          if (file.type.startsWith("image/")) {
            return true
          } else {
            toast.error(t('validation.file_skipped', { fileName: file.name }))
            return false
          }
        })
        .map((file) => ({
          id: Date.now() + Math.random(),
          file,
          preview: URL.createObjectURL(file),
        }))
      setImages((prevImages) => [...prevImages, ...newValidImages])
    }
  }

  const removeImage = (id) => {
    setImages(images.filter((img) => img.id !== id))
  }

  const handleDragPrimary = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActivePrimary(true)
    } else if (e.type === "dragleave") {
      setDragActivePrimary(false)
    }
  }

  const handleDropPrimary = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActivePrimary(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handlePrimaryImageUpload(e.dataTransfer.files)
    }
  }

  const handleDragAdditional = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveAdditional(true)
    } else if (e.type === "dragleave") {
      setDragActiveAdditional(false)
    }
  }

  const handleDropAdditional = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActiveAdditional(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAdditionalImageUpload(e.dataTransfer.files)
    }
  }

  //here 
  const [variantMatrix, setVariantMatrix] = useState({})


  const [variantGroups, setVariantGroups] = useState([{ id: Date.now(), selectedVariants: {} }])

  // const addGroup = () => {
  //   if (variantGroups.length === 0) {
  //     setVariantGroups([{ id: Date.now(), selectedVariants: {} }])
  //   } else {
  //     // Copy the variant types structure from the first group
  //     const firstGroupTypes = Object.keys(variantGroups[0].selectedVariants)
  //     const newSelectedVariants = {}

  //     firstGroupTypes.forEach((type) => {
  //       newSelectedVariants[type] = ""
  //     })

  //     setVariantGroups((prev) => [
  //       ...prev,
  //       {
  //         id: Date.now(),
  //         selectedVariants: newSelectedVariants,
  //       },
  //     ])
  //   }
  // }

  // const toggleVariantType = (type) => {
  //   const newSelectedTypes = selectedVariantTypes.includes(type)
  //     ? selectedVariantTypes.filter((t) => t !== type)
  //     : [...selectedVariantTypes, type]

  //   setSelectedVariantTypes(newSelectedTypes)

  //   // Update all groups to include/exclude this variant type
  //   setVariantGroups((prevGroups) =>
  //     prevGroups.map((group) => {
  //       const newSelectedVariants = { ...group.selectedVariants }

  //       if (newSelectedTypes.includes(type)) {
  //         if (!newSelectedVariants[type]) {
  //           newSelectedVariants[type] = ""
  //         }
  //       } else {
  //         delete newSelectedVariants[type]
  //       }

  //       return {
  //         ...group,
  //         selectedVariants: newSelectedVariants,
  //       }
  //     }),
  //   )
  // }

  // const removeGroup = (id) => {
  //   setVariantGroups((prev) => prev.filter((group) => group.id !== id))
  // }

  // const updateGroupVariant = (groupId, type, variantId) => {
  //   setVariantGroups((prev) =>
  //     prev.map((group) =>
  //       group.id === groupId ? { ...group, selectedVariants: { ...group.selectedVariants, [type]: variantId } } : group,
  //     ),
  //   )
  // }

  // const toggleClient = (clientId) => {
  //   setSelectedClients((prev) => (prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]))
  // }
  const toggleVariantTypeDimension = (type) => {
    setSelectedVariantTypes((prev) => {
      if (prev.includes(type)) {
        if (prev.length === 1) return prev
        return prev.filter((t) => t !== type)
      }
      return [...prev, type]
    })
    setVariantMatrix({})
    setErrors((prev) => ({ ...prev, variantTypes: "", variants: "" }))
  }

  const clearVariantSelectionError = () =>
    setErrors((prev) => ({ ...prev, variants: "" }))

  const toggleCell = (colorId, sizeId) => {
    const k = `${colorId}__${sizeId}`
    setVariantMatrix((prev) => {
      const updated = { ...prev }
      if (updated[k]) delete updated[k]
      else updated[k] = { colorId, sizeId }
      return updated
    })
    clearVariantSelectionError()
  }

  const toggleColorOnly = (colorId) => {
    const k = `c__${colorId}`
    setVariantMatrix((prev) => {
      const updated = { ...prev }
      if (updated[k]) delete updated[k]
      else updated[k] = { colorId, sizeId: null }
      return updated
    })
    clearVariantSelectionError()
  }

  const toggleSizeOnly = (sizeId) => {
    const k = `s__${sizeId}`
    setVariantMatrix((prev) => {
      const updated = { ...prev }
      if (updated[k]) delete updated[k]
      else updated[k] = { colorId: null, sizeId }
      return updated
    })
    clearVariantSelectionError()
  }

  const removeVariantEntry = (k, entry) => {
    if (entry.colorId != null && entry.sizeId != null) {
      toggleCell(entry.colorId, entry.sizeId)
    } else if (entry.colorId != null) {
      toggleColorOnly(entry.colorId)
    } else if (entry.sizeId != null) {
      toggleSizeOnly(entry.sizeId)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = t('validation.product_name_required')
    if (!formData.achat_price) newErrors.achat_price = t('validation.achat_price_required')
    if (!formData.saller_price) newErrors.saller_price = t('validation.seller_price_required')
    if (!formData.selling_price) newErrors.selling_price = t('validation.selling_price_required')
    if (!formData.stock) newErrors.stock = t('validation.stock_required')
    if (!formData.category_id) newErrors.category_id = t('validation.category_required')
    if (!formData.fournisseur_id) newErrors.fournisseur_id = t('validation.fournisseur_required')
    if (!primaryImage) newErrors.primaryImage = t('validation.primary_image_required')

    if (
      !formData.achat_price ||
      !formData.saller_price ||
      !formData.selling_price ||
      Number.parseFloat(formData.saller_price) <= Number.parseFloat(formData.achat_price) ||
      Number.parseFloat(formData.selling_price) <= Number.parseFloat(formData.saller_price) ||
      Number.parseFloat(formData.selling_price) <= Number.parseFloat(formData.achat_price)
    ) {
      newErrors.price = t('validation.price_logic')
    }

    if (!formData.is_global && selectedClients.length === 0) {
      newErrors.clients = t('validation.clients_required')
    }

    if (formData.variant) {
      if (selectedVariantTypes.length === 0) {
        newErrors.variantTypes = t('forms.variant.choose_hint')
      }
      if (Object.keys(variantMatrix).length === 0) {
        newErrors.variants = t('forms.variant.select_option')
      }
    }

    if (has_discount) {
      if (!formData.discount) newErrors.discount = t('validation.discount_required')
      if (!formData.discount_start_date) newErrors.discount_start_date = t('validation.discount_start_date_required')
      if (!formData.discount_end_date) newErrors.discount_end_date = t('validation.discount_end_date_required')
      if (
        formData.discount_start_date &&
        formData.discount_end_date &&
        new Date(formData.discount_start_date) >= new Date(formData.discount_end_date)
      ) {
        newErrors.discount_end_date = t('validation.end_date_after_start')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error(t('messages.validation_failed'))
      return
    }

    setIsLoading(true)

    try {
      const requestFormData = new FormData()

      for (const key in formData) {
        if (key === "is_global" || key === "product_expired_soon") {
          formData[key] === true ? requestFormData.append(key, 1) : requestFormData.append(key, 0)
        } else if (key === "discount" && !has_discount) {
          continue
        } else if (key === "discount_start_date" && !has_discount) {
          continue
        } else if (key === "discount_end_date" && !has_discount) {
          continue
        } else {
          requestFormData.append(key, formData[key])
        }
      }

      if (primaryImage && primaryImage.file) {
        requestFormData.append("image_py", primaryImage.file)
      }

      if (images.length > 0) {
        images.forEach((image) => {
          requestFormData.append("images[]", image.file)
        })
      }

      // if (variantGroups.length > 0 && formData.variant === true) {
      //   requestFormData.append("variants", JSON.stringify(variantGroups))
      // }
      //here
      const selectedPairs = Object.values(variantMatrix)
      if (selectedPairs.length > 0 && formData.variant === true) {
        requestFormData.append("variants", JSON.stringify(selectedPairs))
      }

      if (selectedClients.length > 0 && formData.is_global === false) {
        requestFormData.append("clients", JSON.stringify(selectedClients))
      }

      console.log(Object.entries(requestFormData))

      axios
        .post(BASE_URL + "/api/products", requestFormData, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((result) => {
          if (result.data.code === 200) {
            setSuccess(false)
            toast.success(t('messages.product_created_successfully'))
            setTimeout(() => {
              setFormData({
                name: "",
                description: "",
                achat_price: "",
                saller_price: "",
                selling_price: "",
                stock: "",
                status: "active",
                is_global: true,
                variant: false,
                category_id: "",
                fournisseur_id: "",
                image_urls: "",
                product_expired_soon: false,
                discount: "",
                has_discount: "",
                discount_start_date: "",
                discount_end_date: "",
                rating: 0,
              })
              setPrimaryImage(null)
              setImages([])
              // setSelectedVariants([])
              setVariantMatrix({})
              setSelectedVariantTypes([])
              setSelectedClients([])
              setSuccess(false)
              setErrors({})
              setIsLoading(false)
            }, 1000)
          } else if (result.data.code !== 200) {
            setErrors(result.data.errors)
            toast.error(result.data.message || t('messages.validation_failed'))
            setIsLoading(false)
          } else {
            toast.error(result.data.message || t('messages.unexpected_error'))
            setIsLoading(false)
          }
        })
        .catch((error) => {
          console.error("Network error:", error)
          toast.error(t('messages.network_error_desc'))
          setIsLoading(false)
        })
    } catch (error) {
      console.error("Submission error:", error)
      toast.error(t('messages.unexpected_error'))
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
                <span className="text-xl font-semibold">{t('messages.product_created_successfully') || 'Product Created Successfully!'}</span>
          </div>
              <p className="text-[var(--color-primary-50)]">{t('messages.new_product_added') || 'Your new product has been added to the catalog.'}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('forms.sections.basic_information') || 'Basic Information'}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="name" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('forms.labels.product_name') || 'Product Name *'}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('forms.placeholders.enter_product_name') || 'Enter product name...'}
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
              {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('forms.labels.description') || 'Description'}
              </label>
              <RichTextEditor
                id="description"
                value={formData.description}
                onChange={(html) => {
                  setFormData((prev) => ({ ...prev, description: html }))
                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: "" }))
                  }
                }}
                placeholder={t('forms.placeholders.enter_product_description') || 'Enter product description...'}
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 w-[100%] md:grid-cols-3  gap-4">
              <div className="">
                <label htmlFor="fournisseur_id" className="block text-sm font-semibold text-gray-900 mb-3">
                  {t('forms.labels.fournisseur')}
                </label>
                <select
                  id="fournisseur_id"
                  name="fournisseur_id"
                  value={formData.fournisseur_id}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 cursor-pointer outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.fournisseur_id
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                >
                  <option disabled value="">
                    {t('forms.placeholders.select_fournisseur') || 'Select an Fournisseur'}
                  </option>
                  {Fournisseurs.map((Fournisseur) => (
                    <option key={Fournisseur.id} value={Fournisseur.id}>
                      {Fournisseur.name}
                    </option>
                  ))}
                </select>
                {errors.fournisseur_id && <p className="mt-2 text-red-600 text-xs">{errors.fournisseur_id}</p>}
              </div>

              <div className="">
                <label htmlFor="category_id" className="w-full block text-sm font-semibold text-gray-900 mb-3">
                  {t('forms.labels.category')}
                </label>
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 cursor-pointer outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.category_id
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                >
                  <option disabled value="">
                    {t('forms.placeholders.select_category') || 'Select a category'}
                  </option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
                {errors.category_id && <p className="mt-2 text-red-600 text-xs">{errors.category_id}</p>}
              </div>

              <div className="">
                <label htmlFor="status" className="block text-sm font-semibold text-gray-900 mb-3">
                  {t('forms.labels.status')}
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full py-3 px-4 cursor-pointer text-xs outline-none border-2 border-gray-200 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                >
                  <option value="active">{t('products.status_active')}</option>
                  <option value="inactive">{t('products.status_inactive')}</option>
                  <option value="out_of_stock">{t('products.out_of_stock')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('forms.sections.pricing_inventory') || 'Pricing & Inventory'}</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="achat_price" className="block text-sm font-semibold text-gray-900 mb-3">
                {t('forms.labels.achat_price')}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">DH</span>
                <input
                  type="number"
                  id="achat_price"
                  name="achat_price"
                  value={formData.achat_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-10 py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.achat_price
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.achat_price && <p className="mt-2 text-red-600 text-xs">{errors.achat_price}</p>}
            </div>
            <div>
              <label htmlFor="saller_price" className="block text-sm font-semibold text-gray-900 mb-3">
                {t('forms.labels.seller_price')}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">DH</span>
                <input
                  type="number"
                  id="saller_price"
                  name="saller_price"
                  value={formData.saller_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-10 py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.saller_price
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.saller_price && <p className="mt-2 text-red-600 text-xs">{errors.saller_price}</p>}
            </div>
            <div>
              <label htmlFor="selling_price" className="block text-sm font-semibold text-gray-900 mb-3">
                {t('forms.labels.selling_price')}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">DH</span>
                <input
                  type="number"
                  id="selling_price"
                  name="selling_price"
                  value={formData.selling_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-10 py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.selling_price
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.selling_price && <p className="mt-2 text-red-600 text-xs">{errors.selling_price}</p>}
            </div>

            <div>
              <label htmlFor="stock" className="block text-sm font-semibold text-gray-900 mb-3">
                {t('forms.labels.stock_quantity')}
              </label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.stock
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
              {errors.stock && <p className="mt-2 text-red-600 text-xs">{errors.stock}</p>}
            </div>
            {errors.price && <p className="text-red-600 text-xs w-100">{errors.price}</p>}
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('forms.sections.product_images') || 'Product Images'}</h2>

          <div className="md:col-span-2 mb-6">
            <label className="block font-semibold text-sm text-gray-900 mb-3">{t('forms.labels.primary_image') || 'Primary Image *'}</label>
            {!primaryImage ? (
              <div
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
                  dragActivePrimary
                    ? "border-[var(--color-primary-500)] bg-[var(--color-primary-50)]"
                    : "border-gray-300 hover:border-[var(--color-primary-500)] hover:bg-gray-50"
                }`}
                onDragEnter={handleDragPrimary}
                onDragLeave={handleDragPrimary}
                onDragOver={handleDragPrimary}
                onDrop={handleDropPrimary}
              >
                <input
                  ref={primaryFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files.length > 0 && handlePrimaryImageUpload(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="space-y-4">
                  <div className="w-12 h-12 mx-auto bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                    <svg className="h-7 w-7 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{t('forms.placeholders.drop_primary_image') || 'Drop your primary image here'}</p>
                    <p className="text-gray-500 text-xs">{t('forms.placeholders.or_click_to_browse') || 'or click to browse files'}</p>
                  </div>
                  <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                </div>
              </div>
            ) : (
              <div className="relative bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
                <div className="aspect-square relative">
                  <img src={primaryImage.preview || "/placeholder.svg"} fill className="object-cover" alt="" />
                </div>
                <button
                  type="button"
                  onClick={removePrimaryImage}
                  className="absolute cursor-pointer top-2 right-2 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-all duration-200"
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {errors.primaryImage && <p className="mt-2 text-red-600 text-xs">{errors.primaryImage}</p>}
          </div>

          <div
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 mb-6 ${
              dragActiveAdditional
                ? "border-[var(--color-primary-500)] bg-[var(--color-primary-50)]"
                : "border-gray-300 hover:border-[var(--color-primary-500)] hover:bg-gray-50"
            }`}
            onDragEnter={handleDragAdditional}
            onDragLeave={handleDragAdditional}
            onDragOver={handleDragAdditional}
            onDrop={handleDropAdditional}
          >
            <input
              ref={additionalFileInputRef}
              type="file"
              // accept="image/*"
              multiple
              onChange={(e) => e.target.files.length > 0 && handleAdditionalImageUpload(e.target.files)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            <div className="space-y-4">
              <div className="w-12 h-12 mx-auto bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                <svg className="h-7 w-7 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div>
                <p className=" font-medium text-gray-900">{t('forms.placeholders.drop_additional_images') || 'Drop your additional images here'}</p>
                <p className="text-gray-500 text-xs">{t('forms.placeholders.or_click_to_browse') || 'or click to browse files'}</p>
              </div>
              <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB each</p>
            </div>
          </div>

          {images.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image) => (
                <div key={image.id} className="relative bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
                  <div className="aspect-square relative">
                    <img src={image.preview || "/placeholder.svg"} fill className="object-cover" alt="" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="absolute cursor-pointer top-2 right-2 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-all duration-200"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-2 mb-6">
          <label htmlFor="image_urls" className="block font-semibold text-sm text-gray-900 mb-3">
            {t('forms.labels.additional_image_urls') || 'Additional Image URLs (comma-separated)'}
          </label>
          <input
            type="text"
            id="image_urls"
            name="image_urls"
            value={formData.image_urls}
            onChange={handleInputChange}
            placeholder={t('forms.placeholders.enter_additional_image_urls') || 'Enter additional image URLs, e.g., url1.jpg, url2.png'}
            className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.image_urls
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.image_urls && <p className="mt-2 text-red-600 text-xs">{errors.image_urls}</p>}
        </div>

        <div className="mb-8">
          <div className="my-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="variant"
                checked={formData.variant}
                onChange={handleInputChange}
                className={`appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 ${locale === "ar" ? "checked:before:translate-x-[-100%]" : "checked:before:translate-x-full"} shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5`}
              />
              <span className="font-medium text-sm text-gray-900">
                {t('forms.variant.variant_product')}
                <span className="block text-xs text-gray-500">{t('forms.variant.available_default')}</span>
              </span>
            </label>
          </div>
        
          {formData.variant && (
            <div className="space-y-4">
              {(colorVariants.length > 0 || sizeVariants.length > 0) && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">{t('forms.variant.dimensions')}</p>
                  <div className="flex flex-wrap gap-4">
                    {colorVariants.length > 0 && (
                      <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={selectedVariantTypes.includes("color")}
                          onChange={() => toggleVariantTypeDimension("color")}
                          className={`appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 ${locale === "ar" ? "checked:before:translate-x-[-100%]" : "checked:before:translate-x-full"} shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5`}
                          />
                        {t('forms.variant.color')}
                      </label>
                    )}
                    {sizeVariants.length > 0 && (
                      <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={selectedVariantTypes.includes("size")}
                          onChange={() => toggleVariantTypeDimension("size")}
                          className={`appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 ${locale === "ar" ? "checked:before:translate-x-[-100%]" : "checked:before:translate-x-full"} shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5`}
                          />
                        {t('forms.variant.size')}
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('forms.variant.choose_hint')} {t('forms.variant.select_option')}
                  </p>
                  {errors.variantTypes && <p className="mt-2 text-red-600 text-xs">{errors.variantTypes}</p>}
                </div>
              )}

              {selectedVariantTypes.includes("color") && selectedVariantTypes.includes("size") && (
                <div className="overflow-x-auto">
                  {colorVariants.length === 0 || sizeVariants.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      {t('forms.variant.add_color_size')}
                    </p>
                  ) : (
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr>
                          <th className="text-left p-3 text-gray-500 font-medium border-b border-gray-200 min-w-32">
                            {t('forms.variant.color_size')}
                          </th>
                          {sortedSizeVariants.map((size) => (
                            <th key={size.id} className="p-3 text-center border-b border-gray-200">
                              <span className="inline-block px-2 py-1 bg-gray-100 rounded-lg font-medium text-gray-700">
                                {size.value}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {colorVariants.map((color) => (
                          <tr key={color.id} className="border-b border-gray-100">
                            <td className="flex gap-2 p-3 border-r border-gray-200 font-medium text-gray-800">
                              <span
                                className="inline-block w-5 h-5 rounded-md border border-gray-300 align-middle"
                                style={{ backgroundColor: color.value }}
                              />
                              {color.name}
                            </td>
                            {sortedSizeVariants.map((size) => {
                              const k = `${color.id}__${size.id}`
                              const isChecked = !!variantMatrix[k]
                              return (
                                <td
                                  key={size.id}
                                  className={`p-3 text-center transition-colors`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleCell(color.id, size.id)}
                                    className="
                                    w-5 h-5 
                                    cursor-pointer 
                                    appearance-none 
                                    border-2 
                                    rounded-md 
                                    checked:bg-[var(--color-primary-500)] 
                                    checked:border-[var(--color-primary-500)]
                                    border-gray-300 
                                    hover:border-[var(--color-primary-500)]
                                    transition-all 
                                    duration-200 
                                    ease-in-out
                                    relative
                                    checked:after:content-['✓']
                                    checked:after:text-white
                                    checked:after:text-sm
                                    checked:after:font-bold
                                    checked:after:absolute
                                    checked:after:top-1/2
                                    checked:after:left-1/2
                                    checked:after:-translate-x-1/2
                                    checked:after:-translate-y-1/2
                                    focus:ring-2 
                                    focus:ring-[var(--color-primary-500)] 
                                    focus:ring-offset-2
                                    focus:outline-none
                                  "
                                  />
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {selectedVariantTypes.includes("color") && !selectedVariantTypes.includes("size") && (
                <div className="overflow-x-auto">
                  {colorVariants.length === 0 ? (
                    <p className="text-xs text-amber-600">{t('forms.variant.no_colors')}</p>
                  ) : (
                    <table className="w-full border-collapse text-xs max-w-md">
                      <thead>
                        <tr>
                          <th className="text-left p-3 text-gray-500 font-medium border-b border-gray-200">{t('forms.variant.color')}</th>
                          <th className="p-3 text-center border-b border-gray-200 w-24">{t('forms.variant.include')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {colorVariants.map((color) => {
                          const k = `c__${color.id}`
                          const isChecked = !!variantMatrix[k]
                          return (
                            <tr key={color.id} className="border-b border-gray-100">
                              <td className="flex gap-2 p-3 font-medium text-gray-800">
                                <span
                                  className="inline-block w-5 h-5 rounded-md border border-gray-300 align-middle"
                                  style={{ backgroundColor: color.value }}
                                />
                                {color.name}
                              </td>
                              <td className={`p-3 text-center`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleColorOnly(color.id)}
                                  className="
                                    w-5 h-5 
                                    cursor-pointer 
                                    appearance-none 
                                    border-2 
                                    rounded-md 
                                    checked:bg-[var(--color-primary-500)] 
                                    checked:border-[var(--color-primary-500)]
                                    border-gray-300 
                                    hover:border-[var(--color-primary-500)]
                                    transition-all 
                                    duration-200 
                                    ease-in-out
                                    relative
                                    checked:after:content-['✓']
                                    checked:after:text-white
                                    checked:after:text-sm
                                    checked:after:font-bold
                                    checked:after:absolute
                                    checked:after:top-1/2
                                    checked:after:left-1/2
                                    checked:after:-translate-x-1/2
                                    checked:after:-translate-y-1/2
                                    focus:ring-2 
                                    focus:ring-[var(--color-primary-500)] 
                                    focus:ring-offset-2
                                    focus:outline-none
                                  "
                                />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {!selectedVariantTypes.includes("color") && selectedVariantTypes.includes("size") && (
                <div className="overflow-x-auto">
                  {sizeVariants.length === 0 ? (
                    <p className="text-xs text-amber-600">{t('forms.variant.no_sizes')}</p>
                  ) : (
                    <table className="w-full border-collapse text-xs max-w-md">
                      <thead>
                        <tr>
                          <th className="text-left p-3 text-gray-500 font-medium border-b border-gray-200">{t('forms.variant.size')}</th>
                          <th className="p-3 text-center border-b border-gray-200 w-24">{t('forms.variant.include')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSizeVariants.map((size) => {
                          const k = `s__${size.id}`
                          const isChecked = !!variantMatrix[k]
                          return (
                            <tr key={size.id} className="border-b border-gray-100">
                              <td className="p-3 font-medium text-gray-800">
                                <span className="inline-block px-2 py-1 bg-gray-100 rounded-lg">{size.value}</span>
                              </td>
                              <td className={`p-3 text-center`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleSizeOnly(size.id)}
                                  className="
                                    w-5 h-5 
                                    cursor-pointer 
                                    appearance-none 
                                    border-2 
                                    rounded-md 
                                    checked:bg-[var(--color-primary-500)] 
                                    checked:border-[var(--color-primary-500)]
                                    border-gray-300 
                                    hover:border-[var(--color-primary-500)]
                                    transition-all 
                                    duration-200 
                                    ease-in-out
                                    relative
                                    checked:after:content-['✓']
                                    checked:after:text-white
                                    checked:after:text-sm
                                    checked:after:font-bold
                                    checked:after:absolute
                                    checked:after:top-1/2
                                    checked:after:left-1/2
                                    checked:after:-translate-x-1/2
                                    checked:after:-translate-y-1/2
                                    focus:ring-2 
                                    focus:ring-[var(--color-primary-500)] 
                                    focus:ring-offset-2
                                    focus:outline-none
                                  "
                                />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {errors.variants && <p className="text-red-600 text-xs">{errors.variants}</p>}

              {Object.keys(variantMatrix).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(variantMatrix)
                    .sort(([, a], [, b]) => {
                      const colorA = a.colorId != null
                        ? availableVariants.find((v) => v.id === a.colorId)
                        : null

                      const colorB = b.colorId != null
                        ? availableVariants.find((v) => v.id === b.colorId)
                        : null

                      const sizeA = a.sizeId != null
                        ? availableVariants.find((v) => v.id === a.sizeId)
                        : null

                      const sizeB = b.sizeId != null
                        ? availableVariants.find((v) => v.id === b.sizeId)
                        : null

                      // 1️⃣ Sort by color name
                      const colorCompare = (colorA?.name || "").localeCompare(colorB?.name || "")
                      if (colorCompare !== 0) return colorCompare

                      // 2️⃣ Then by size value
                      return (sizeA?.value || "").localeCompare(sizeB?.value || "")
                    })
                    .map(([k, entry]) => {
                      const color = entry.colorId != null
                        ? availableVariants.find((v) => v.id === entry.colorId)
                        : null

                      const size = entry.sizeId != null
                        ? availableVariants.find((v) => v.id === entry.sizeId)
                        : null

                      const label =
                        color && size
                          ? `${color.name} / ${size.value}`
                          : color
                          ? color.name
                          : size
                          ? size.value
                          : k

                      const chipStyle =
                        color && !size
                          ? {
                              backgroundColor: color.value + "22",
                              borderColor: color.value + "55",
                              color: color.value,
                            }
                          : {
                              borderColor: "#e5e7eb",
                              backgroundColor: "#f9fafb",
                              color: "#374151",
                            }

                      return (
                        <span
                          key={k}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border"
                          style={chipStyle}
                        >
                          {color && (
                            <span
                              className="w-2 h-2 rounded-full inline-block"
                              style={{ backgroundColor: color.value }}
                            />
                          )}
                          {label}
                          <button
                            type="button"
                            onClick={() => removeVariantEntry(k, entry)}
                            className="ml-1 opacity-60 hover:opacity-100 cursor-pointer"
                          >
                            ×
                          </button>
                        </span>
                      )
                    })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="my-8">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="is_global"
              checked={formData.is_global}
              onChange={handleInputChange}
              className={`appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 ${locale === "ar" ? "checked:before:translate-x-[-100%]" : "checked:before:translate-x-full"} shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5`}
              />
            <span className="font-medium text-sm text-gray-900">
              {t('forms.global_product')}
              <span className="block text-xs text-gray-500">{t('forms.available_all_default')}</span>
            </span>
          </label>
        </div>

        {!formData.is_global && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-6">{t('forms.client_assignment')}</h2>
            <div className="bg-gray-50 rounded-2xl p-6">
              <p className="text-gray-600 mb-4 text-xs">{t('forms.select_clients')}</p>

              <Select
                isMulti
                options={availableClients.map((client) => ({
                  value: client.id,
                  label: `${client.info.name} (${client.info.name_store}) `,
                }))}
                value={availableClients
                  .filter((client) => selectedClients.includes(client.id))
                  .map((client) => ({
                    value: client.id,
                    label: `${client.info.name} (${client.info.name_store})`,
                  }))}
                onChange={(selectedOptions) => {
                  setSelectedClients(selectedOptions.map((option) => option.value))
                }}
                className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "transparent",
                    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
                    border: "none",
                    cursor: "pointer",
                  }),
                }}
                placeholder={t('forms.search_clients')}
                noOptionsMessage={() => t('forms.no_clients_found')}
                isSearchable
              />

              {errors.clients && <p className="mt-2 text-red-600 text-xs">{errors.clients}</p>}

              {/* Show selected clients as chips */}
              {selectedClients.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {availableClients
                    .filter((client) => selectedClients.includes(client.id))
                    .map((client) => (
                      <div
                        key={client.id}
                        className="flex items-center text-[var(--color-primary-500)] bg-[var(--color-primary-50)] border border-[var(--color-primary-500)] rounded-full px-3  text-[11px]"
                      >
                        <span>
                          {client.info.name} ({client.info.name_store})
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedClients((prev) => prev.filter((id) => id !== client.id))}
                          className="ml-2 text-lg text-[var(--color-primary-500)] hover:text-[var(--color-primary-50)]0 cursor-pointer"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="mb-10">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="product_expired_soon"
              checked={formData.product_expired_soon}
              onChange={handleInputChange}
              className={`appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 ${locale === "ar" ? "checked:before:translate-x-[-100%]" : "checked:before:translate-x-full"} shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5`}
              />
            <span className="font-medium text-sm text-gray-900">{t('forms.product_expired_soon')}</span>
          </label>
        </div>

        <div className="mb-12">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="has_discount"
              checked={has_discount}
              onChange={() => {
                setHas_discount((prev) => !prev)
              }}
              className={`appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 ${locale === "ar" ? "checked:before:translate-x-[-100%]" : "checked:before:translate-x-full"} shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5`}
              />
            <span className="font-medium text-sm text-gray-900">{t('forms.apply_discount')}</span>
          </label>

          {has_discount && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="">
                <label htmlFor="discount" className="block font-semibold text-sm text-gray-900 mb-3">
                  {t('forms.labels.discount_percent')}
                </label>
                <input
                  type="number"
                  min="0"
                  id="discount"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  placeholder={t('forms.labels.discount_percent')}
                  className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.discount
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {errors.discount && <p className="mt-2 text-red-600 text-xs">{errors.discount}</p>}
              </div>
              <div>
                <label htmlFor="discount_start_date" className="block text-sm font-semibold text-gray-900 mb-3">
                  {t('forms.labels.discount_start_date')}
                </label>
                <input
                  type="date"
                  id="discount_start_date"
                  name="discount_start_date"
                  value={formData.discount_start_date}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.discount_start_date
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {errors.discount_start_date && (
                  <p className="mt-2 text-red-600 text-xs">{errors.discount_start_date}</p>
                )}
              </div>
              <div>
                <label htmlFor="discount_end_date" className="block text-sm font-semibold text-gray-900 mb-3">
                  {t('forms.labels.discount_end_date')}
                </label>
                <input
                  type="date"
                  id="discount_end_date"
                  name="discount_end_date"
                  value={formData.discount_end_date}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.discount_end_date
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {errors.discount_end_date && <p className="mt-2 text-red-600 text-xs">{errors.discount_end_date}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Product Rating */}
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('forms.product_rating')}</h2>
          <div>
            <label htmlFor="rating" className="block text-sm font-semibold text-gray-900 mb-3">
              {t('forms.rating_stars')}
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`h-6 w-6 cursor-pointer ${star <= formData.rating ? "text-yellow-400" : "text-gray-300"}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  onClick={() => setFormData({ ...formData, rating: star })}
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.683-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.565-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <span className="ml-2 text-sm text-gray-600">
                {formData.rating > 0 ? `${formData.rating} ${formData.rating > 1 ? t('products.star_plural') : t('products.star_singular')}` : t('products.no_rating')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <Link
            to="/products"
            className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
          >
            <button
              type="button"
              disabled={isLoading}
              className={`cursor-pointer w-full ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {t('buttons.cancel')}
            </button>
          </Link>
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed opacity-75"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('buttons.saving')}
              </span>
            ) : (
              <span className="flex items-center justify-center">
                {t('buttons.save')}
                <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

